import { Axis, Quaternion, Vector3 } from '@babylonjs/core';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { ObstacleBehavior } from './ObstacleBehavior';

enum HammerState {
  IDLE,
  JUMP,
  WINDUP,
  SMASH,
  IMPACT,
  RECOVER,
}

export class ObstacleHammerBehavior extends ObstacleBehavior {
  private state: HammerState = HammerState.IDLE;
  private timer: number = 0;

  // Configs
  private jumpHeight: number = 3;

  // State durations
  private readonly IDLE_TIME = 2.0;
  private readonly JUMP_TIME = 1.0;
  private readonly WINDUP_TIME = 1.4;
  private readonly SMASH_TIME = 0.2;
  private readonly RECOVER_TIME = 1.0;

  // Animation values
  private initialRotationQuaternion: Quaternion;
  private initialPosition: Vector3;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleHammer', startPosition, pathIndex, config);
    this.jumpHeight = config.height ?? 3;
  }

  attach(mesh: any): void {
    super.attach(mesh);
    if (!mesh.rotationQuaternion) {
      mesh.rotationQuaternion = Quaternion.FromEulerVector(mesh.rotation);
    }
    this.initialRotationQuaternion = mesh.rotationQuaternion.clone();
    this.initialPosition = mesh.position.clone();
  }

  update(deltaTime: number): void {
    this.timer += deltaTime;

    switch (this.state) {
      case HammerState.IDLE:
        // Stand still
        if (this.timer > this.IDLE_TIME) {
          this.switchState(HammerState.JUMP);
        }
        break;

      case HammerState.JUMP:
        {
          const t = Math.min(this.timer / this.JUMP_TIME, 1);
          // Jump up
          // We want to end at the peak, so we use half of PI (0 to 1)
          const height = Math.sin(t * (Math.PI / 2)) * this.jumpHeight;
          this.mesh.position.y = this.initialPosition.y + height;

          if (t >= 1) {
            this.switchState(HammerState.WINDUP);
          }
        }
        break;

      case HammerState.WINDUP:
        {
          // Rotate back (lean back to smash)
          const t = Math.min(this.timer / this.WINDUP_TIME, 1);
          const targetAngle = -Math.PI / 4; // 45 degrees back

          const currentAngle = targetAngle * t;
          const rotationOffset = Quaternion.RotationAxis(Axis.X, currentAngle);

          this.mesh.rotationQuaternion = this.initialRotationQuaternion.multiply(rotationOffset);

          // Maintain height
          this.mesh.position.y = this.initialPosition.y + this.jumpHeight;

          if (t >= 1) {
            this.switchState(HammerState.SMASH);
          }
        }
        break;

      case HammerState.SMASH:
        {
          const t = Math.min(this.timer / this.SMASH_TIME, 1);

          // Rotation: From -45deg to +90deg (flat on ground)
          const startAngle = -Math.PI / 4;
          const endAngle = Math.PI / 2;
          const currentAngle = startAngle + (endAngle - startAngle) * t;

          const rotationOffset = Quaternion.RotationAxis(Axis.X, currentAngle);
          this.mesh.rotationQuaternion = this.initialRotationQuaternion.multiply(rotationOffset);

          // Position: From Peak to Ground
          const startY = this.initialPosition.y + this.jumpHeight;
          const endY = this.initialPosition.y;
          this.mesh.position.y = startY + (endY - startY) * (t * t); // Accelerate down

          if (t >= 1) {
            this.impact();
            this.switchState(HammerState.RECOVER);
          }
        }
        break;

      case HammerState.RECOVER:
        {
          // return to start
          const t = Math.min(this.timer / this.RECOVER_TIME, 1);

          // Interpolate position back to start (should be there already from smash, but just in case)
          this.mesh.position.y = this.initialPosition.y;

          // Interpolate rotation back to 0
          // We ended at PI/2
          const startAngle = Math.PI / 2;
          const endAngle = 0;
          const currentAngle = startAngle + (endAngle - startAngle) * t;

          const rotationOffset = Quaternion.RotationAxis(Axis.X, currentAngle);
          this.mesh.rotationQuaternion = this.initialRotationQuaternion.multiply(rotationOffset);

          if (this.timer > this.RECOVER_TIME) {
            // Hard reset ensuring perfect alignment
            this.mesh.rotationQuaternion.copyFrom(this.initialRotationQuaternion);
            this.mesh.position.copyFrom(this.initialPosition);
            this.switchState(HammerState.IDLE);
          }
        }
        break;
    }
  }

  private switchState(newState: HammerState) {
    this.state = newState;
    this.timer = 0;
  }

  private impact() {
    if (!this.level) return;

    this.level.game.sound.play('explosion', this.mesh);

    // Particles!
    // Using existing movement particles as smoke/dust
    // Using impact dust particles
    const system = this.level.particles.createImpactDustParticleSystem(this.mesh);
    system.start();

    // Check player collision for damage
    const dist = Vector3.Distance(this.mesh.position, this.level.player.getPosition());
    if (dist < 3) {
      // Close enough to be squashed
      if (this.level.player.vulnerable && !this.level.game.config.godMode) {
        this.level.player.die();
      }
    }
  }

  clone(): ObstacleHammerBehavior {
    return new ObstacleHammerBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
